import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { NEVER, Observable, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BasicDialogComponent } from '../shared/components/basic-dialog/basic-dialog.component';
import config from '../../config';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SimpleDialog } from '../shared/models/simple-dialog';

/**
 * Paths served by the app itself, which must never carry a bearer token.
 *
 * This is an explicit allow-list of path prefixes rather than a substring test.
 * `url.includes('/auth/')` also matches a protected API route such as
 * `/todos/auth/reauthorise`, which would silently strip authentication from it.
 */
const PUBLIC_PATH_PREFIXES = ['/auth/', '/login', '/assets/'] as const;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private dialog: MatDialog,
    public router: Router
  ) {}

  /**
   * True when the request targets the protected API and therefore requires a
   * bearer token.
   *
   * `API_GATEWAY_ENDPOINT` is injected at build time and falls back to `""`.
   * A naive `url.includes(config.API_GATEWAY_ENDPOINT)` is then always true,
   * because every string contains the empty string, so a misconfigured build
   * would route every request - including public ones - through the auth flow.
   */
  private isApiRequest(url: string): boolean {
    const endpoint = config.API_GATEWAY_ENDPOINT;
    if (!endpoint) {
      return false;
    }
    return url.includes(endpoint);
  }

  private isPublicPath(url: string): boolean {
    let path = url;
    try {
      path = new URL(url, window.location.origin).pathname;
    } catch {
      // Relative or malformed URL - fall back to matching the raw value.
    }
    return PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
  }

  /**
   * Stops the request and sends the user back through login.
   *
   * Returning NEVER means the request is never dispatched. Dispatching it
   * without an `Authorization` header produces a 401 from the API Gateway
   * Cognito authorizer, and because API Gateway error responses carried no CORS
   * headers the browser discarded them - surfacing to the user as an opaque
   * "0 Unknown Error" instead of an expired session.
   */
  private rejectAndReauthenticate(): Observable<never> {
    const simpleDialog: SimpleDialog = {
      uiTitle: 'Your session has expired',
      uiMessage:
        "To continue, please click 'OK' to be redirected to the login page.",
      redirect: '/auth/logout',
    };
    this.dialog.open(BasicDialogComponent, { data: simpleDialog });
    return NEVER;
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const originalUrl = req.url;

    // Public assets and the auth flow itself must not carry a token.
    if (this.isPublicPath(originalUrl) || !this.isApiRequest(originalUrl)) {
      return next.handle(req);
    }

    return from(fetchAuthSession()).pipe(
      switchMap((session) => {
        const accessToken = session.tokens?.accessToken?.toString();
        const idToken = session.tokens?.idToken;
        const tokenExpirationTime = idToken?.payload.exp;
        const now = Math.floor(Date.now() / 1000);

        if (tokenExpirationTime && tokenExpirationTime <= now) {
          return this.rejectAndReauthenticate();
        }

        if (!accessToken) {
          return this.rejectAndReauthenticate();
        }

        return next.handle(
          req.clone({
            headers: req.headers.set('Authorization', accessToken),
          })
        );
      }),
      catchError(() => this.rejectAndReauthenticate())
    );
  }
}
